import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { View, Text, Button, InputField, Card } from "../base";
import { Icon } from "../base/Icon";
import { useAppDispatch, useAppSelector } from "../../redux";
import { sessionActions } from "../../redux/reducers/sessionSlice";
import { makeRequest } from "../../utils/request";

const RESEND_COOLDOWN_SECONDS = 60;

export const EmailVerificationScreen = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAppSelector((state) => state.session.user);

  const paramCode = searchParams.get("verify_code");

  const [code, setCode] = useState(paramCode || "");
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const autoVerifyAttempted = useRef(false);

  // Clean up URL params
  useEffect(() => {
    if (paramCode) {
      searchParams.delete("verify_code");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Send initial verification email on mount (skip if we have params from email link)
  useEffect(() => {
    if (!paramCode) {
      sendVerificationEmail();
    }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Auto-verify if code came from email link
  useEffect(() => {
    if (paramCode && !autoVerifyAttempted.current) {
      autoVerifyAttempted.current = true;
      handleVerify();
    }
  }, [paramCode]);

  const sendVerificationEmail = useCallback(async () => {
    if (isSending || cooldown > 0) return;
    setIsSending(true);
    setError(null);

    try {
      await makeRequest("/v1/auth/verify/send", {
        method: "POST",
      });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const message = (err as Error).message || "";
      if (message.includes("Please wait before requesting another verification code")) {
        setError("Please check your email for an existing code or wait a minute before resending.");
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setError("Failed to send verification email. Please try again.");
      }
    } finally {
      setIsSending(false);
    }
  }, [isSending, cooldown]);

  const handleVerify = async () => {
    if (!code.trim()) return;
    setIsVerifying(true);
    setError(null);

    try {
      await makeRequest("/v1/auth/verify/confirm", {
        method: "POST",
        body: { code: code.trim() },
      });
      // Reload session to get updated emailVerified status
      await dispatch(sessionActions.getSession());
    } catch (err) {
      setError((err as Error).message || "Invalid verification code. Please try again.");
      setIsVerifying(false);
    }
  };

  const handleSignOut = () => {
    navigate("/signout");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleVerify();
    }
  };

  return (
    <View className="flex flex-col items-center justify-center w-full h-full px-4">
      <Card className="flex flex-col items-center gap-4 p-8 max-w-md w-full">
        <Icon
          name="mail"
          className="text-brand text-4xl mb-2"
        />
        <Text className="text-2xl font-semibold text-gray-900 dark:text-white text-center">Verify your email</Text>
        <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">
          We sent a verification code to{" "}
          <Text
            as="span"
            className="font-semibold text-gray-900 dark:text-white"
          >
            {user?.email}
          </Text>
        </Text>

        <View className="w-full space-y-4 mt-2">
          <InputField
            label="Verification code"
            placeholder="Enter the code from your email"
            value={code}
            onChange={(value) => {
              setCode(value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            error={error || undefined}
          />

          <Button
            onClick={handleVerify}
            isLoading={isVerifying}
            isDisabled={!code.trim()}
            className="w-full"
          >
            Verify
          </Button>

          <Button
            variant="ghost"
            onClick={sendVerificationEmail}
            isLoading={isSending}
            isDisabled={cooldown > 0}
            className="w-full"
          >
            {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
          </Button>
        </View>

        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="mt-2"
        >
          <Icon
            name="logout"
            className="text-lg mr-2"
          />
          <Text>Sign out</Text>
        </Button>
      </Card>
    </View>
  );
};
